import React, { useEffect, useState } from "react";
import { Shield, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function NewRuleDialog({ open, onOpenChange, departments, categories, onSaved }) {
  const blank = () => ({
    department_id: "", product_category: "",
    floor_pct: "", ceiling_pct: "", severity: "soft", note: "",
  });
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(blank()); }, [open]);

  const submit = async () => {
    if (!form.floor_pct && !form.ceiling_pct) {
      toast.error("Set a floor or ceiling"); return;
    }
    setBusy(true);
    try {
      await v2Api.marginRules.create({
        department_id: form.department_id || null,
        product_category: form.product_category.trim() || null,
        floor_pct: form.floor_pct ? Number(form.floor_pct) : null,
        ceiling_pct: form.ceiling_pct ? Number(form.ceiling_pct) : null,
        severity: form.severity,
        note: form.note.trim() || null,
      });
      toast.success("Rule added");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New margin rule</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div>
            <Label className="text-xs text-slate-500">Department (blank = workspace-wide)</Label>
            <select value={form.department_id} onChange={set("department_id")}
                    className="w-full px-2 py-1 border rounded-md bg-white text-sm">
              <option value="">— all departments —</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Product category (blank = all categories)</Label>
            <select
              value={form.product_category}
              onChange={set("product_category")}
              className="w-full px-2 py-1 border rounded-md bg-white text-sm"
            >
              <option value="">— all categories —</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-slate-500">Floor %</Label>
              <Input type="number" value={form.floor_pct} onChange={set("floor_pct")} placeholder="e.g. 6" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Ceiling %</Label>
              <Input type="number" value={form.ceiling_pct} onChange={set("ceiling_pct")} />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Severity</Label>
            <select value={form.severity} onChange={set("severity")}
                    className="w-full px-2 py-1 border rounded-md bg-white text-sm">
              <option value="soft">soft (warn)</option>
              <option value="hard">hard (block at quote)</option>
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Note (shown on the warning chip)</Label>
            <Input value={form.note} onChange={set("note")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MarginRulesPage() {
  const [rules, setRules] = useState([]);
  const [depts, setDepts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [r, d, prods] = await Promise.all([
        v2Api.marginRules.list(),
        v2Api.departments(),
        v2Api.catalog.products(),
      ]);
      setRules(r || []);
      setDepts(d || []);
      // Dedupe categories from the catalog so users pick from real options.
      const cats = Array.from(new Set((prods || []).map((p) => p.category).filter(Boolean))).sort();
      setCategories(cats);
    } catch { toast.error("Could not load rules"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const remove = async (r) => {
    if (!await confirm("Delete this rule?", { title: "Delete rule", confirmLabel: "Delete" })) return;
    try { await v2Api.marginRules.remove(r.id); refresh(); }
    catch { toast.error("Delete failed"); }
  };
  const toggleActive = async (r) => {
    try { await v2Api.marginRules.patch(r.id, { is_active: !r.is_active }); refresh(); }
    catch { toast.error("Update failed"); }
  };
  const deptName = (id) => depts.find((d) => d.id === id)?.name || "—";

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" /> Margin rules
          </h1>
          <p className="text-sm text-slate-500">
            Soft warnings (or hard blocks at quotation time) when a BoQ line's
            margin breaches the configured floor or ceiling. Department + category
            rules override workspace-wide defaults.
          </p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="w-4 h-4 mr-1" /> Add rule
        </Button>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      {!loading && rules.length === 0 && (
        <Card><CardContent className="py-10 text-center text-slate-400">
          No rules yet. The default is "no floor". Add one to start enforcing margins.
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {rules.map((r) => (
          <Card key={r.id} className={r.is_active ? "" : "opacity-60"}>
            <CardContent className="py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <Badge className={r.severity === "hard"
                    ? "bg-rose-100 text-rose-700 border-rose-200"
                    : "bg-amber-100 text-amber-700 border-amber-200"}>
                    {r.severity}
                  </Badge>
                  <span className="font-mono">
                    {r.floor_pct != null && `floor ≥ ${r.floor_pct}%`}
                    {r.floor_pct != null && r.ceiling_pct != null && " · "}
                    {r.ceiling_pct != null && `ceiling ≤ ${r.ceiling_pct}%`}
                  </span>
                  {r.department_id && <Badge variant="outline">{deptName(r.department_id)}</Badge>}
                  {r.product_category && <Badge variant="outline">{r.product_category}</Badge>}
                  {!r.department_id && !r.product_category && (
                    <span className="text-[10px] text-slate-400 italic">workspace default</span>
                  )}
                </div>
                {r.note && <p className="text-xs text-slate-500 mt-1">{r.note}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => toggleActive(r)}
                        className={`text-[10px] px-2 py-0.5 rounded-full border ${
                          r.is_active
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-slate-100 text-slate-500 border-slate-200"
                        }`}>
                  {r.is_active ? "active" : "inactive"}
                </button>
                <button onClick={() => remove(r)} className="text-slate-300 hover:text-rose-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <NewRuleDialog
        open={open}
        onOpenChange={setOpen}
        departments={depts}
        categories={categories}
        onSaved={refresh}
      />
    </div>
  );
}
